import React, { useCallback, useEffect, useState } from 'react';
import {
  DataGridPro,
  GridColDef,
  GridComparatorFn,
  GridOverlay,
  GridSortModel,
  GridFilterModel,
  getGridDateOperators,
} from '@mui/x-data-grid-pro';
import { Alert, Card, MenuItem, Link as MuiLink, Select, SelectChangeEvent, Snackbar, Tooltip } from '@mui/material';
import Button from '@mui/material/Button';
import { styled } from '@mui/material/styles';
import PlagiarismIcon from '@mui/icons-material/Plagiarism';
import RateReviewIcon from '@mui/icons-material/RateReview';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';

import { getAssessmentAdminPanelRows } from '../../services/assessmentAdminPanel';
import {
  AssessmentAdminPanelRow,
  QcReviewStatus,
  QC_REVIEW_STATUS_LABELS,
} from '../../interfaces/assessmentAdminPanel.interface';
import { fetchAssessment, updateAssessmentSurveySimple, updateAssessmentSingleKey, processAssessmentsReportRequest } from '../../services/assessments';
import moment from 'moment';
import { getCompany, updateCompanyBoolean } from '../../services/company';
import { Survey } from '../../interfaces/survey.interface';
import { updateContactShort } from '../../services/contact';
import { updateCompanyApplicationShort } from '../../services/companyApplication';
import { AssessmentSurvey } from '../../interfaces/assessment.interface';
import User, { Version } from '../../interfaces/users.interface';
import { getUsersByType } from '../../app/store/usersSlice';
import { useAppDispatch, useAppSelector } from '../../app/hooks';
import CircularProgress from '@mui/material/CircularProgress';
import { createBadge, updateVersionBadge } from '../../services/applications';
import { WORKFLOW_STATUSES, fetchWorkflows, isWorkflow531OrHigher, updateWorkflow } from '../../services/workflow';
import { USER_TYPES } from '../../services/user';
import BaseToolbar from '../Datagrid/BaseToolbar';
import GridPaginationBar from './GridPaginationBar';
import { GOLD_BADGE_LABEL, GOLD_NEXT_GEN_BADGE_LABEL, GOLD_STAR_BADGE_LABEL, SILVER_BADGE_LABEL, eligibleForGoldStarBadge, eligibleForNextGenGoldBadge, processVerificationAward } from '../../services/badge';
import { Workflow } from '../../interfaces/workflow.interface';
import { normalizeFilterModel, sameModel, stringContainsOnly } from '../../utils/adminDashboardUtils';
import ReportRequestButton from './ReportRequestButton';
import AssessmentActionsCell, { ApproveState } from './AssessmentActionsCell';
import ContinueQcReviewModal from './QC/ContinueQcReviewModal';
import FinalizeAssessmentModal from './QC/FinalizeAssessmentModal';
import QcRunDetailsModal from './QC/QcRunDetailsModal';
import QcPreviewModal from './QC/QcPreviewModal';
import EditQcScoreModal from './Modals/EditQcScoreModal';
import PointManagerModal from './Modals/PointManagerModal';
import { triggerAssessmentQC, updateWorkflowQcPoints, updateWorkflowQcScore } from '../../services/qcReview';
import { ACCENT_BLUE, ACCENT_BLUE_DARK, SUCCESS_GREEN } from '../Assessments/Workspace/qcTheme';

let assessorUsers:User[] = [];

interface AssessmentRow {
  id: number;
  vendorName: string;
  assessorCompanyName: string;
  assessorName: string;
  assessorLevel: string;
  siteOrApplicationName: string;
  link: string;
  status: string;
  adminReviewer: number;
  submittedDate: string;
  qcReviewStatus: QcReviewStatus;
  qcFirstPassDone: boolean;
  qcRound: number | null;
  qcIssuesTotal: number;
  qcIssuesApproved: number;
  qcScore: number | null;
  qcScoreEmailed: number | null;
  qcPointsDeducted: number | null;
  qcPointsTotal: number | null;
  qcStatusAt: string | null;
  survey: number;
  assessmentId?: number;
  assessmentStatus?: string;
  workflowStatus?: string;
  workflowId?: number;
}

function CustomToolbar(props: { showCompletedAssessmentSurveys: boolean; toggleShowCompletedAssessmentSurveys: () => void }) {
  const { showCompletedAssessmentSurveys, toggleShowCompletedAssessmentSurveys, ...rest } = props;

  const handleProcessAssessmentsReportRequest = useCallback(async () => {
    try {
      await processAssessmentsReportRequest();
    } catch (error) {
      console.error('Failed to process assessments report request', error);
      throw error;
    }
  }, []);

  return (
    <BaseToolbar
    {...rest}
    exportExtraItems={
      <ReportRequestButton
        type="Assessments"
        onRequest={handleProcessAssessmentsReportRequest}
      />
    }
    csvOptions={{ fileName: `tpn_in_review_assessments_${new Date().toLocaleDateString()}` }}
  >
    <Button variant="text" size="small" onClick={toggleShowCompletedAssessmentSurveys}>
      Show {showCompletedAssessmentSurveys ? 'Submitted' : 'Completed'} Assessments
      </Button>
    </BaseToolbar>
  );
}

const isEmail = /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$/gi;

function selectAdmin(assRow: AssessmentRow) {
  const handleSetReviewer = (event: SelectChangeEvent) => {
    if (!event || !event.target.value) return;
    const user = event.target.value;
    updateAssessmentSurveySimple(assRow.id, { adminReviewer: user });
  };

  return (<>
    { !assessorUsers && assRow.adminReviewer }
    { assessorUsers &&
      <Select
        labelId="company-primary-contact"
        id="company-primary-contact-select"
        label="Primary Contact"
        onChange={handleSetReviewer}
        defaultValue={assRow.adminReviewer?.toString()}
        fullWidth
      >
        {assessorUsers.filter(e => e.firstName?.length > 0 || e.lastName?.length > 0).map((admin: User) =>
          <MenuItem key={admin.id} value={admin.id} selected={true}>{admin.firstName} {admin.lastName}</MenuItem>,
        )}
      </Select>
    }
  </>);
}

const dateComparator: GridComparatorFn<Date> = (v1, v2) => new Date(v1).getTime() - new Date(v2).getTime();

// Maps the flat pre-joined /assessment-admin-panel/ row onto the grid row - the backend pre-annotates everything.
// QC review-status chip palette, matching the workspace's status vocabulary.
const QC_STATUS_CHIP: Record<QcReviewStatus, { fg: string; bg: string; border: string }> = {
  initial_review: { fg: '#b26a00', bg: 'rgba(239,148,30,0.14)', border: 'rgba(239,148,30,0.45)' },
  tpn_review: { fg: ACCENT_BLUE_DARK, bg: 'rgba(25,118,210,0.12)', border: 'rgba(25,118,210,0.4)' },
  pending_assessor: { fg: '#5f6368', bg: 'rgba(138,143,152,0.14)', border: 'rgba(138,143,152,0.42)' },
  qc_approved: { fg: SUCCESS_GREEN, bg: 'rgba(46,125,50,0.12)', border: 'rgba(46,125,50,0.4)' },
};

// A button: any status past Initial Review deep-links into Continue Review.
const ADVANCED_ASSESSOR_LEVEL = 'Advanced TPN Assessor';

const ASSESSOR_LEVEL_ABBREVIATIONS: Record<string, string> = {
  [ADVANCED_ASSESSOR_LEVEL]: 'ADV',
  'TPN Assessor': 'TPN',
};

// Stands in for the old Assessor Level column; createAssessmentRow writes 'Null' when the API sends nothing — also a no-badge case.
const assessorLevelAbbreviation = (level: string): string => {
  if (!level || level === 'Null') return '';
  return ASSESSOR_LEVEL_ABBREVIATIONS[level] || level.split(' ')[0].slice(0, 4).toUpperCase();
};

const AssessorCell = styled('span')({
  display: 'inline-flex',
  alignItems: 'center',
  gap: '8px',
  minWidth: 0,
});

const AssessorNameText = styled('span')({
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
});

const LevelBadge = styled('span', {
  shouldForwardProp: prop => prop !== 'advanced',
})<{ advanced: boolean }>(({ advanced }) => ({
  display: 'inline-flex',
  alignItems: 'center',
  flexShrink: 0,
  height: '19px',
  padding: '0 7px',
  borderRadius: '10px',
  fontSize: '0.625rem',
  fontWeight: 700,
  letterSpacing: '0.5px',
  color: advanced ? ACCENT_BLUE : '#546e7a',
  background: advanced ? 'rgba(25,118,210,0.12)' : 'rgba(84,110,122,0.1)',
}));

const QcStatusChip = styled('button', {
  shouldForwardProp: prop => prop !== 'status',
})<{ status: QcReviewStatus }>(({ status }) => ({
  display: 'inline-flex',
  alignItems: 'center',
  gap: '6px',
  height: '24px',
  padding: '0 10px',
  borderRadius: '12px',
  fontSize: '0.75rem',
  fontWeight: 600,
  whiteSpace: 'nowrap',
  fontFamily: 'inherit',
  cursor: 'pointer',
  color: QC_STATUS_CHIP[status].fg,
  background: QC_STATUS_CHIP[status].bg,
  border: `1px solid ${QC_STATUS_CHIP[status].border}`,
  '& .MuiSvgIcon-root': { fontSize: '14px' },
  '&:disabled': { cursor: 'default' },
}));

const QC_STATUS_ICONS: Record<QcReviewStatus, JSX.Element> = {
  initial_review: <PlagiarismIcon />,
  tpn_review: <RateReviewIcon />,
  pending_assessor: <HourglassEmptyIcon />,
  qc_approved: <CheckCircleIcon />,
};

// Time the row has sat in its current QC status — the grid's ageing signal.
function formatDaysInStatus(statusAt: string | null): string {
  if (!statusAt) return '';
  const days = Math.max(0, Math.round((Date.now() - new Date(statusAt).getTime()) / 86400000));
  if (days === 0) return 'Today';
  return `${days} ${days === 1 ? 'day' : 'days'}`;
}

// Point/score/round columns are meaningless until QC has actually started.
const beforeQc = (row: AssessmentRow): boolean => row.qcReviewStatus === 'initial_review';

// Proposal when no effective score is recorded yet — pending the rubric spec.
const PROPOSED_SCORE_FALLBACK = 87;

function createAssessmentRow(apiRow: AssessmentAdminPanelRow): AssessmentRow {
  if (apiRow.assessmentId !== null && !apiRow.submittedDate) {
    updateAssessmentSurveySimple(apiRow.id, { submittedDate: apiRow.updatedAt });
  }

  return {
    id: apiRow.id,
    vendorName: apiRow.companyName || '',
    assessorCompanyName: apiRow.assessorCompanyName || '',
    assessorName: apiRow.assessorName || '',
    assessorLevel: apiRow.assessorLevel || 'Null',
    siteOrApplicationName: apiRow.siteAppName || '',
    link: apiRow.assessmentId !== null ? `/assessment/${apiRow.assessmentId}/questionnaire/${apiRow.survey}` : '-',
    status: apiRow.status,
    adminReviewer: apiRow.adminReviewer as number,
    submittedDate: apiRow.submittedDate ? moment(apiRow.submittedDate).format('MM/DD/YYYY') : '-',
    // Default guards against an older API during deploy skew — never crash the chip.
    qcReviewStatus: apiRow.qcReviewStatus || 'initial_review',
    qcFirstPassDone: apiRow.qcFirstPassDone ?? false,
    qcRound: apiRow.qcRound,
    qcIssuesTotal: apiRow.qcIssuesTotal ?? 0,
    qcIssuesApproved: apiRow.qcIssuesApproved ?? 0,
    qcScore: apiRow.qcScore,
    qcScoreEmailed: apiRow.qcScoreEmailed ?? null,
    qcPointsDeducted: apiRow.qcPointsDeducted,
    qcPointsTotal: apiRow.qcPointsTotal,
    qcStatusAt: apiRow.qcStatusAt,
    survey: apiRow.survey,
    assessmentId: apiRow.assessmentId ?? undefined,
    assessmentStatus: apiRow.assessmentStatus ?? undefined,
    workflowStatus: apiRow.workflowStatus ?? undefined,
    workflowId: apiRow.workflowId ?? undefined,
  };
}

export default function AssessmentAdminPanel(props: { assessmentsAddressed: any }) {
  const dispatch = useAppDispatch();
  assessorUsers = useAppSelector(state => state.users).results || [];
  const { user: currentUser } = useAppSelector(state => state.user);
  const adminDisplayName = currentUser
    ? [currentUser.firstName, currentUser.lastName].filter(Boolean).join(' ') || currentUser.email || 'TPN Admin'
    : 'TPN Admin';
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(25);
  const [rowCount, setRowCount] = useState(0);

  const [rows, setRows] = useState<Array<AssessmentRow>>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [savingRowId, setSavingRowId] = useState<number | null>(null);
  const [scoreRow, setScoreRow] = useState<AssessmentRow | null>(null);
  const [continueRow, setContinueRow] = useState<AssessmentRow | null>(null);
  const [pointsRow, setPointsRow] = useState<AssessmentRow | null>(null);
  const [finalizeRow, setFinalizeRow] = useState<AssessmentRow | null>(null);
  const [runDetailsRow, setRunDetailsRow] = useState<AssessmentRow | null>(null);
  const [previewRow, setPreviewRow] = useState<AssessmentRow | null>(null);
  const [runQcBusyId, setRunQcBusyId] = useState<number | null>(null);
  const [runQcNotice, setRunQcNotice] = useState<{ severity: 'success' | 'error'; message: string } | null>(null);

  const [sortModel, setSortModel] = useState<GridSortModel>([]);
  const [filterModelDraft, setFilterModelDraft] = useState<GridFilterModel>({ items: [] });
  const [filterModelApplied, setFilterModelApplied] = useState<GridFilterModel>({ items: [] });
  const [showCompletedAssessmentSurveys, setShowCompletedAssessmentSurveys] = useState(false);

  const toggleShowCompletedAssessmentSurveys = () => {
    setPage(0);
    setShowCompletedAssessmentSurveys(v => !v);
  };

  const fetchAndSetRows = useCallback(async () => {
    try {
      setIsLoading(true);

      const statuses = showCompletedAssessmentSurveys
        ? ['Complete', 'Remediation']
        : ['Submitted'];

      const resp = await getAssessmentAdminPanelRows({
        statuses,
        page,
        pageSize,
        sortModel,
        filterModel: filterModelApplied,
      });

      setRowCount(resp.count);
      setRows(resp.results.map(createAssessmentRow));

      props.assessmentsAddressed();
    } catch (error) {
      console.error('Error fetching assessment survey assessments:', error);
    } finally {
      setIsLoading(false);
    }
  }, [page, pageSize, sortModel, filterModelApplied, showCompletedAssessmentSurveys]);

  async function approveAssessmentRow(assRow: AssessmentRow) {
    const theRow = { ...assRow };

    const awardBadgesIfNoRemediations = async (
      workflow: Workflow,
      newDate: moment.Moment,
      status: string,
      versionId?: number,
    ) => {
      const isWf531OrGreater = isWorkflow531OrHigher(workflow.workflowType);
      const badgeIdSet = new Set<number>(workflow.badges ? workflow.badges.map(b => typeof b === 'number' ? b : b.id!) : []);

      try {
        if (isWf531OrGreater) {
          // Award Silver, NextGen Gold and Gold Plus
          const silverBadge = await createBadge({
            title: SILVER_BADGE_LABEL,
            awardedDate: newDate.format('YYYY-MM-DD'),
            expirationDate: moment(newDate.format('YYYY-MM-DD')).add(2, 'years').format('YYYY-MM-DD'),
          });
          badgeIdSet.add(silverBadge.id!);

          const nextGenEligible = await eligibleForNextGenGoldBadge(workflow.id);
          if (nextGenEligible) {
            const nextGenBadge = await processVerificationAward(GOLD_NEXT_GEN_BADGE_LABEL, workflow.id);
            if (nextGenBadge) badgeIdSet.add(nextGenBadge.id!);
          }

          const goldStarEligible = await eligibleForGoldStarBadge(workflow.id);
          if (goldStarEligible) {
            const goldStarBadge = await processVerificationAward(GOLD_STAR_BADGE_LABEL, workflow.id);
            if (goldStarBadge) badgeIdSet.add(goldStarBadge.id!);
            if (versionId && goldStarBadge) await updateVersionBadge(versionId, goldStarBadge.id!);
          }
        } else {
          // Award Classic Gold
          const classicGoldBadge = await createBadge({
            title: GOLD_BADGE_LABEL,
            awardedDate: newDate.format('YYYY-MM-DD'),
            expirationDate: moment(newDate.format('YYYY-MM-DD')).add(2, 'years').format('YYYY-MM-DD'),
          });
          badgeIdSet.add(classicGoldBadge.id!);
          if (versionId) await updateVersionBadge(versionId, classicGoldBadge.id!);
        }
      } catch (error) {
        console.error('Error processing badges:', error);
      } finally {
        updateWorkflow({ id: workflow.id, badges: [...Array.from(badgeIdSet)], reportIssueDate: newDate.format('YYYY-MM-DD'), status });
      }
    };

    const approveAssessment = async () => {
      if (!theRow.assessmentId) return;
      setSavingRowId(theRow.id);

      // Deep data (full Assessment + sibling surveys, company, workflow) is only needed at
      // approve-click-time, not on every grid page load - fetch it fresh here instead of
      // preloading it for every row.
      const assessment = await fetchAssessment(theRow.assessmentId);
      const assessmentSurvey = (assessment.surveys as AssessmentSurvey[]).find(as => as.id === theRow.id);
      if (!assessmentSurvey) {
        setSavingRowId(null);
        return;
      }

      let status = assessmentSurvey.bestPracticeRemediationCount > 0 || assessmentSurvey.implementationGuidanceRemediationCount > 0 ? 'Remediation' : 'Complete';
      const companyId = typeof assessment.company === 'number' ? assessment.company : assessment.company.id!;
      const company = await getCompany(companyId.toString());
      const workflow = await fetchWorkflows({ assessment_survey: assessmentSurvey.id }, true);
      const newDate = moment().utc();
      updateAssessmentSurveySimple(
        assessmentSurvey.id!,
        {
          status,
          reportIssueDate: newDate.format('YYYY-MM-DDTHH:mm:ssZZ'),
          expirationDate: newDate.clone().startOf('day').add(2, 'years').format('YYYY-MM-DDTHH:mm:ssZZ'),
        }).then(() => {
        setSavingRowId(null);
        // A finalized survey leaves the Submitted worklist: drop it and refetch to backfill; on the Completed view it stays.
        if (showCompletedAssessmentSurveys) {
          setRows(prev => prev.map(row => (row.id === theRow.id ? { ...row, status } : row)));
          return;
        }
        setRows(prev => prev.filter(row => row.id !== theRow.id));
        setRowCount(prev => Math.max(0, prev - 1));
        fetchAndSetRows();
      }).catch((error) => {
        console.error('Error updating assessment survey:', error);
        setSavingRowId(null);
      });

      let assessmentStatus = status;

      (assessment.surveys as AssessmentSurvey[]).forEach(assSur => {
        if (assessmentSurvey.id === assSur.id || ['Remediation', 'Complete'].indexOf(assessmentStatus) === -1) return;
        if (['Remediation', 'Complete'].indexOf(assSur.status) === -1) {
          assessmentStatus = assSur.status;
          return;
        }
        if (['Remediation', 'Complete'].indexOf(assSur.status) !== -1) {
          if (assSur.status === 'Remediation' && assessmentStatus === 'Complete') {
            assessmentStatus = 'Remediation';
            return;
          }
        }
      });

      if (['Remediation', 'Complete'].indexOf(assessmentStatus) !== -1) updateAssessmentSingleKey(assessment.id!, 'status', assessmentStatus);
      if (assessmentStatus === 'Remediation') return updateWorkflow({ id: workflow[0].id, reportIssueDate: newDate.format('YYYY-MM-DD'), status: assessmentStatus });
      updateCompanyBoolean({ id: company.id, displayShieldType: 'tpn_assessed', goldShieldDate: newDate.format('YYYY-MM-DD') });
      if ((assessmentSurvey.survey as Survey).site) {
        updateContactShort({ id: (assessmentSurvey.survey as Survey).site, badge: 'tpn_assessed', goldShieldDate: newDate.format('YYYY-MM-DD') });
        await awardBadgesIfNoRemediations(workflow[0], newDate, assessmentStatus);
      }
      if ((assessmentSurvey.survey as Survey).application) {
        const companyApplication = company.companyApplications?.find(ca => ca.application.id === (assessmentSurvey.survey as Survey).application);
        if (!companyApplication) return;

        const version = (companyApplication.application.versions as Version[]).find(v => v.value === (assessmentSurvey.survey as Survey).version);
        const date = moment().utc();
        if (version) {
          await awardBadgesIfNoRemediations(workflow[0], newDate, assessmentStatus, version.id);
        }

        if (companyApplication.badge !== 'tpn_assessed') updateCompanyApplicationShort({ id: companyApplication.id, badge: 'tpn_assessed', goldShieldDate: date });
      }
    };

    await approveAssessment();
  }

  const allIssuesApproved = (assRow: AssessmentRow): boolean =>
    assRow.qcIssuesTotal > 0 && assRow.qcIssuesApproved === assRow.qcIssuesTotal;

  // Only unapproved QC issues block finalization — pre-QC-loop assessments raised none and pass freely.
  const finalizeBlockedReason = (assRow: AssessmentRow): string | undefined => {
    if (!assRow.workflowId) return 'No workflow found for this assessment';
    if (assRow.qcIssuesTotal > 0 && !allIssuesApproved(assRow)) {
      return `${assRow.qcIssuesTotal - assRow.qcIssuesApproved} QC issue(s) still to approve`;
    }
    return undefined;
  };

  const approveStateFor = (assRow: AssessmentRow): ApproveState => {
    if (['Remediation', 'Complete', 'Completed'].indexOf(assRow.status) > -1) return 'approved';
    if (savingRowId === assRow.id) return 'saving';
    return finalizeBlockedReason(assRow) ? 'blocked' : 'ready';
  };

  // Finalize is the only completion point; the Approve Assessment dialog always gates it — score and assessor email send from that confirmation only.
  const handleFinalizeClick = (assRow: AssessmentRow) => {
    setFinalizeRow(assRow);
  };

  const qcRunEligibility = (assRow: AssessmentRow) => {
    const notSubmitted: string[] = [];
    if (assRow.workflowStatus !== WORKFLOW_STATUSES.SUBMITTED) notSubmitted.push(`workflow (${assRow.workflowStatus || 'unknown'})`);
    if (assRow.assessmentStatus !== WORKFLOW_STATUSES.SUBMITTED) notSubmitted.push(`assessment (${assRow.assessmentStatus || 'unknown'})`);
    if (assRow.status !== WORKFLOW_STATUSES.SUBMITTED) notSubmitted.push(`assessment survey (${assRow.status})`);

    if (assRow.qcFirstPassDone) {
      return { eligible: false, reason: 'QC has already run on this assessment' };
    }

    const eligible = notSubmitted.length === 0 && assRow.workflowId !== undefined;
    const reason = eligible ? undefined : notSubmitted.length > 0
      ? `Not yet Submitted: ${notSubmitted.join(', ')}`
      : 'No workflow found for this assessment survey';
    return { eligible, reason };
  };

  // Fire-and-forget: the backend enqueues the run and emails the admin when ready — the grid never polls QCRun.status.
  const handleRunQc = async (assRow: AssessmentRow) => {
    if (!assRow.workflowId) return;
    setRunQcBusyId(assRow.id);
    try {
      await triggerAssessmentQC(assRow.workflowId);
      setRunQcNotice({ severity: 'success', message: 'QC started. You\u2019ll be emailed when it\u2019s ready to review.' });
    } catch (error: any) {
      console.error('Failed to trigger QC run', error);
      const detail = Array.isArray(error?.response?.data) ? error.response.data[0] : undefined;
      setRunQcNotice({ severity: 'error', message: detail || 'Failed to start the QC run. Please try again.' });
    } finally {
      setRunQcBusyId(null);
    }
  };

  // Score/point edits persist through the QC API (actor stamp + audit log); the row patches locally for instant feedback.
  const handleSaveScore = async (score: number, reason: string) => {
    const row = scoreRow;
    setScoreRow(null);
    if (!row?.workflowId) return;
    try {
      await updateWorkflowQcScore(row.workflowId, score, reason);
      setRows(prev => prev.map(r => (r.id === row.id ? { ...r, qcScore: score } : r)));
    } catch (error) {
      console.error('Failed to update QC score', error);
    }
  };

  const handleSavePoints = async (pointsDeducted: number, pointsTotal: number) => {
    const row = pointsRow;
    setPointsRow(null);
    if (!row?.workflowId) return;
    try {
      await updateWorkflowQcPoints(row.workflowId, pointsDeducted, pointsTotal);
      setRows(prev => prev.map(r => (
        r.id === row.id ? { ...r, qcPointsDeducted: pointsDeducted, qcPointsTotal: pointsTotal } : r
      )));
    } catch (error) {
      console.error('Failed to update QC points', error);
    }
  };

  const columns: GridColDef[] = [
    {
      field: 'vendorName',
      filterOperators: stringContainsOnly,
      headerName: 'Vendor',
      width: 300,
    },
    {
      field: 'siteOrApplicationName',
      filterOperators: stringContainsOnly,
      headerName: 'Site or Application Name',
      width: 300,
    },
    {
      field: 'assessorCompanyName',
      filterOperators: stringContainsOnly,
      headerName: 'Assessor Company',
      width: 300,
    },
    {
      field: 'assessorName',
      filterOperators: stringContainsOnly,
      headerName: 'Assessor',
      width: 300,
      renderCell: params => {
        const assRow = params.row as AssessmentRow;
        const abbreviation = assessorLevelAbbreviation(assRow.assessorLevel);
        return (
          <AssessorCell>
            <AssessorNameText>
              {!isEmail.test(assRow.assessorName)
                ? assRow.assessorName
                : <MuiLink href={`mailto:${assRow.assessorName}`}>{assRow.assessorName}</MuiLink>}
            </AssessorNameText>
            {abbreviation && (
              <Tooltip title={assRow.assessorLevel}>
                <LevelBadge advanced={assRow.assessorLevel === ADVANCED_ASSESSOR_LEVEL}>
                  {abbreviation}
                </LevelBadge>
              </Tooltip>
            )}
          </AssessorCell>
        );
      },
    },
    {
      field: 'submittedDate',
      headerName: 'Date Submitted',
      width: 150,
      type: 'dateTime',
      filterOperators: getGridDateOperators().filter(op => op.value === 'is'),
      sortComparator: dateComparator,
      valueFormatter: params => moment(new Date(params?.value)).format('MM/DD/YYYY'),
    },
    {
      field: 'adminReviewer',
      headerName: 'Assigned Reviewer',
      sortable: false,
      filterable: false,
      width: 300,
      renderCell: params => selectAdmin(params.row), valueFormatter: ({ value }) => {
        if (!value) return;
        const admin = assessorUsers.find(u => u.id === value);
        if (!admin) return;
        return `${admin.firstName} ${admin.lastName}`;
      },
    },
    {
      field: 'qcScore',
      headerName: 'Score',
      width: 100,
      type: 'number',
      filterable: false,
      renderCell: params => (
        beforeQc(params.row) || params.row.qcScore === null ? '—' : `${params.row.qcScore}%`
      ),
    },
    {
      field: 'qcRound',
      headerName: 'Round',
      width: 90,
      type: 'number',
      filterable: false,
      renderCell: params => (
        beforeQc(params.row) || !params.row.qcRound ? '—' : String(params.row.qcRound)
      ),
    },
    {
      field: 'qcIssuesApproved',
      headerName: 'Approved Issues',
      width: 140,
      filterable: false,
      sortable: false,
      renderCell: params => {
        const row = params.row as AssessmentRow;
        if (beforeQc(row) || !row.qcIssuesTotal) return '—';
        return (
          <Tooltip title={`${row.qcIssuesApproved} of ${row.qcIssuesTotal} QC issues approved`}>
            <span>{row.qcIssuesApproved}/{row.qcIssuesTotal}</span>
          </Tooltip>
        );
      },
    },
    {
      field: 'qcReviewStatus',
      headerName: 'QC Review Status',
      width: 180,
      sortable: false,
      filterable: false,
      renderCell: params => {
        const assRow = params.row as AssessmentRow;
        const status = assRow.qcReviewStatus;
        const clickable = status !== 'initial_review' && !!assRow.workflowId;
        return (
          <QcStatusChip
            type='button'
            status={status}
            disabled={!clickable}
            title={clickable ? 'Continue Review' : undefined}
            onClick={() => clickable && setContinueRow(assRow)}
          >
            {QC_STATUS_ICONS[status]}
            {QC_REVIEW_STATUS_LABELS[status]}
          </QcStatusChip>
        );
      },
      valueFormatter: ({ value }) => QC_REVIEW_STATUS_LABELS[value as QcReviewStatus] || '',
    },
    {
      field: 'qcStatusAt',
      headerName: 'Days in Status',
      width: 130,
      filterable: false,
      renderCell: params => (
        <Tooltip title='Time in the current review status'>
          <span>{formatDaysInStatus((params.row as AssessmentRow).qcStatusAt)}</span>
        </Tooltip>
      ),
      valueFormatter: ({ value }) => formatDaysInStatus(value as string | null),
    },
    {
      field: 'actions',
      headerName: 'Actions',
      sortable: false,
      filterable: false,
      disableExport: true,
      width: 90,
      renderCell: params => (
        <AssessmentActionsCell
          assessmentSurveyId={params.row.id}
          fallbackName={params.row.siteOrApplicationName}
          canEditQc={!!params.row.workflowId}
          assessmentLink={params.row.link !== '-' ? params.row.link : undefined}
          onContinueReview={() => setContinueRow(params.row as AssessmentRow)}
          continueReviewDisabled={beforeQc(params.row as AssessmentRow)}
          onQcRunDetails={() => setRunDetailsRow(params.row as AssessmentRow)}
          onRunQc={() => handleRunQc(params.row as AssessmentRow)}
          runQcEligible={qcRunEligibility(params.row as AssessmentRow).eligible}
          runQcIneligibleReason={qcRunEligibility(params.row as AssessmentRow).reason}
          runQcBusy={runQcBusyId === params.row.id}
          onPreviewRun={() => setPreviewRow(params.row as AssessmentRow)}
          onEditScore={() => setScoreRow(params.row as AssessmentRow)}
          onPointManager={() => setPointsRow(params.row as AssessmentRow)}
          onApprove={() => handleFinalizeClick(params.row as AssessmentRow)}
          approveState={approveStateFor(params.row as AssessmentRow)}
          approveBlockedReason={finalizeBlockedReason(params.row as AssessmentRow)}
        />
      ),
    },
  ];

  useEffect(() => {
    dispatch(getUsersByType(USER_TYPES.TPN_ADMIN));
  }, []);

  useEffect(() => {
    fetchAndSetRows();
  }, [fetchAndSetRows]);

  return (
    <Card sx={{ padding: '2em' }}>
      <DataGridPro
        style={{ height: 400, width: '100%' }}
        rows={rows}
        columns={columns}
        getRowId={(row) => row.id}
        loading={isLoading}
        pagination
        paginationMode="server"
        rowCount={rowCount}
        page={page}
        pageSize={pageSize}
        rowsPerPageOptions={[25, 50, 100]}
        onPageChange={(newPage) => setPage(newPage)}
        onPageSizeChange={(newPageSize) => {
          setPageSize(newPageSize);
          setPage(0);
        }}
        sortingMode="server"
        sortModel={sortModel}
        onSortModelChange={(model) => {
          setPage(0);
          setSortModel(model);
        }}
        filterMode="server"
        filterModel={filterModelDraft}
        onFilterModelChange={(model) => {
          setPage(0);
          setFilterModelDraft(model);
          const normalized = normalizeFilterModel(model);
          if (!sameModel(normalized, filterModelApplied)) {
            setFilterModelApplied(normalized);
          }
        }}
        components={{
          Toolbar: CustomToolbar,
          Footer: GridPaginationBar,
          LoadingOverlay: () => (
            <GridOverlay>
              <div style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)' }}>
                <CircularProgress />
              </div>
            </GridOverlay>
          ),
        }}
        componentsProps={{
          toolbar: {
            showCompletedAssessmentSurveys,
            toggleShowCompletedAssessmentSurveys,
            csvOptions: { fileName: `tpn_in_review_assessments_${new Date().toLocaleDateString()}` },
          },
        }}
      />
      <ContinueQcReviewModal
        open={!!continueRow}
        row={continueRow}
        onClose={() => setContinueRow(null)}
      />
      <FinalizeAssessmentModal
        open={!!finalizeRow}
        workflowId={finalizeRow?.workflowId ?? null}
        assessmentId={finalizeRow?.assessmentId}
        surveyId={finalizeRow?.survey}
        companyName={finalizeRow?.vendorName}
        unitLabel={finalizeRow?.siteOrApplicationName}
        assessorName={finalizeRow?.assessorName}
        proposedScore={finalizeRow?.qcScore ?? PROPOSED_SCORE_FALLBACK}
        onApproved={() => {
          const approvedRow = finalizeRow;
          setFinalizeRow(null);
          if (!approvedRow) return;
          setRows(prev => prev.map(r => (r.id === approvedRow.id ? { ...r, qcReviewStatus: 'qc_approved' } : r)));
          approveAssessmentRow(approvedRow);
        }}
        onClose={() => setFinalizeRow(null)}
      />
      <QcPreviewModal
        open={!!previewRow}
        assessmentSurveyId={previewRow?.id ?? 0}
        assessmentId={previewRow?.assessmentId}
        surveyId={previewRow?.survey}
        subject={previewRow ? `${previewRow.vendorName} \u00b7 ${previewRow.siteOrApplicationName}` : ''}
        onClose={() => setPreviewRow(null)}
      />
      <Snackbar
        open={!!runQcNotice}
        autoHideDuration={5000}
        onClose={() => setRunQcNotice(null)}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert onClose={() => setRunQcNotice(null)} severity={runQcNotice?.severity ?? 'success'} variant="filled">
          {runQcNotice?.message}
        </Alert>
      </Snackbar>
      <QcRunDetailsModal
        open={!!runDetailsRow}
        workflowId={runDetailsRow?.workflowId}
        subject={runDetailsRow ? `${runDetailsRow.vendorName} · ${runDetailsRow.siteOrApplicationName}` : ''}
        onClose={() => setRunDetailsRow(null)}
      />
      <EditQcScoreModal
        open={!!scoreRow}
        subject={scoreRow ? `${scoreRow.vendorName} · ${scoreRow.siteOrApplicationName}` : ''}
        currentScore={scoreRow?.qcScore ?? null}
        emailedScore={scoreRow?.qcScoreEmailed ?? null}
        adminName={adminDisplayName}
        onSave={handleSaveScore}
        onClose={() => setScoreRow(null)}
      />
      <PointManagerModal
        open={!!pointsRow}
        subject={pointsRow ? `${pointsRow.vendorName} · ${pointsRow.siteOrApplicationName}` : ''}
        pointsDeducted={pointsRow?.qcPointsDeducted ?? null}
        pointsTotal={pointsRow?.qcPointsTotal ?? null}
        adminName={adminDisplayName}
        onSave={handleSavePoints}
        onClose={() => setPointsRow(null)}
      />
    </Card>
  );
}
